import type { DesignReferenceDto } from '@carbonwebmail/contracts'
import {
  decodedBytes,
  MOST_REFERENCE_BYTES,
  MOST_REFERENCE_BYTES_TOTAL,
  MOST_REFERENCES,
  readingOf,
} from '@carbonwebmail/domain'

/**
 * Turning files somebody picked into references a turn can carry.
 *
 * **Everything that can be refused is refused here**, before an upload starts:
 * the type, the size of each, and the size of all of them. The server checks
 * the same three, because a browser is not a guard — but a person who learns
 * their 40MB PDF is too large *after* waiting for it to upload has been made to
 * wait for a no.
 *
 * The bytes travel base64 inside the ordinary JSON turn rather than as a
 * multipart upload. The request already carries the whole conversation, these
 * are a few megabytes at most, and nothing is stored — so a second transport
 * and a temporary file to clean up would exist to save a third of the bytes.
 */

export interface Attached {
  readonly references: readonly DesignReferenceDto[]
  /** What was refused and why, to show beside the picker. One line each. */
  readonly refused: readonly string[]
}

export async function attach(
  chosen: readonly File[],
  already: readonly DesignReferenceDto[],
): Promise<Attached> {
  const references: DesignReferenceDto[] = []
  const refused: string[] = []
  let total = already.reduce((sum, one) => sum + decodedBytes(one.data), 0)

  for (const file of chosen) {
    if (already.length + references.length >= MOST_REFERENCES) {
      refused.push(`Only ${MOST_REFERENCES} files at a time — “${file.name}” was left out.`)
      continue
    }

    const reading = readingOf(file.type)
    if (reading.kind === undefined) {
      refused.push(`“${file.name}”: ${reading.because}.`)
      continue
    }

    if (file.size > MOST_REFERENCE_BYTES) {
      refused.push(`“${file.name}” is larger than ${asMegabytes(MOST_REFERENCE_BYTES)}.`)
      continue
    }

    if (total + file.size > MOST_REFERENCE_BYTES_TOTAL) {
      refused.push(
        `“${file.name}” would take the attachments past ${asMegabytes(MOST_REFERENCE_BYTES_TOTAL)}.`,
      )
      continue
    }

    total += file.size
    references.push({ name: file.name, mediaType: file.type, data: await base64(file) })
  }

  return { references, refused }
}

/**
 * The file's bytes, base64.
 *
 * Built from the array buffer in chunks rather than
 * `String.fromCharCode(...bytes)`, which passes every byte as an argument and
 * overflows the call stack somewhere around a hundred thousand of them — a
 * limit a logo is under and a PDF is not.
 */
async function base64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  const CHUNK = 8192
  let binary = ''

  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK))
  }

  return btoa(binary)
}

function asMegabytes(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))}MB`
}
